import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SearchInput from "../common/SearchInput/SearchInput";
import Dropdown from "../common/Dropdown/Dropdown";
import BlogCard from "./BlogCard";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import "./Blog.scss";
import { useDebounce } from "../../hooks/useDebounce";
import supabase from "../../lib/supabaseClient";

const ITEMS_PER_PAGE = 9;

const Blog = () => {
  const { t, i18n } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const debouncedSearch = useDebounce(searchTerm, 500);

  const sortOptions = [
    { value: "newest", label: i18n.language === "fa" ? "جدیدترین" : "Newest" },
    {
      value: "oldest",
      label: i18n.language === "fa" ? "قدیمی‌ترین" : "Oldest",
    },
    {
      value: "alphabetical",
      label: i18n.language === "fa" ? "الفبایی" : "Alphabetical",
    },
  ];

  const fetchBlogs = async (pageNum = 1, reset = false) => {
    try {
      setLoading(true);

      let query = supabase.from("blogs").select("*").eq("is_published", true);

      // Search
      if (debouncedSearch) {
        const searchPattern = `%${debouncedSearch}%`;
        query = query.or(
          `title_fa.ilike.${searchPattern},title_en.ilike.${searchPattern},description_fa.ilike.${searchPattern},description_en.ilike.${searchPattern},author.ilike.${searchPattern}`,
        );
      }

      // Sort
      if (sortBy === "newest") {
        query = query.order("created_at", { ascending: false });
      } else if (sortBy === "oldest") {
        query = query.order("created_at", { ascending: true });
      } else if (sortBy === "alphabetical") {
        const titleField = i18n.language === "fa" ? "title_fa" : "title_en";
        query = query.order(titleField, { ascending: true });
      }

      // Pagination
      const from = (pageNum - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;
      query = query.range(from, to);

      const { data, error } = await query;

      if (error) throw error;

      if (reset) {
        setBlogs(data || []);
      } else {
        setBlogs((prev) => [...prev, ...(data || [])]);
      }

      setHasMore(data && data.length === ITEMS_PER_PAGE);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setBlogs([]);
    fetchBlogs(1, true);
  }, [debouncedSearch, sortBy, i18n.language]);

  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchBlogs(nextPage, false);
    }
  };

  useInfiniteScroll(loadMore, hasMore, loading);

  return (
    <section className="blog-section">
      <div className="blog-container">
        <div className="blog-header">
          <h1 className="blog-title">
            {/* {i18n.language === "fa" ? "بلاگ" : "Blog"} */}
          </h1>

          <div className="blog-controls">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={
                i18n.language === "fa"
                  ? "جستجو در بلاگ‌ها..."
                  : "Search blogs..."
              }
            />

            <Dropdown
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              placeholder={i18n.language === "fa" ? "مرتب‌سازی" : "Sort by"}
            />
          </div>
        </div>

        {loading && page === 1 ? (
          <div className="blog-loading">
            <div className="spinner"></div>
          </div>
        ) : blogs.length === 0 ? (
          <div className="blog-empty">
            {i18n.language === "fa" ? "موردی یافت نشد" : "No blogs found"}
          </div>
        ) : (
          <div className="blog-list">
            {blogs.map((blog) => (
              <BlogCard key={blog.id} blog={blog} />
            ))}
          </div>
        )}

        {loading && page > 1 && (
          <div className="blog-loading-more">
            <div className="spinner small"></div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Blog;
